// components/BottomNavButton.tsx
'use client';

import { useEffect, useState } from 'react';
import CurrencyDisplay from '../common/CurrencyDisplay';
import { BackendCurrency } from '@/utils/currencyHelpers';
import { FloatingBookingForm } from '@/components/experience-detail/FloatingBookingForm';
import { ExperienceType } from '@/components/experience-detail/types';

type BottomNavButtonProps = {
  scrollToId: string;
  label: string;
  amount: number;
  amountby: string;
  backendCurrency?: BackendCurrency;
  backendConversionRate?: number;
  experience?: unknown;
  type?: ExperienceType;
  onTravelerCountChange?: (count: number) => void;
};

const BottomNavButton: React.FC<
  BottomNavButtonProps & { hideAtTop?: boolean }
> = ({
  scrollToId,
  label,
  amount,
  amountby,
  backendCurrency,
  backendConversionRate,
  experience,
  type,
  onTravelerCountChange,
  hideAtTop = false,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  useEffect(() => {
    const targetSection = document.getElementById(scrollToId);
    if (!targetSection) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(!entry.isIntersecting),
      { threshold: 0.1 }
    );

    observer.observe(targetSection);
    return () => observer.disconnect();
  }, [scrollToId]);

  useEffect(() => {
    if (!hideAtTop) {
      setIsScrolled(true);
      return;
    }

    const handleScroll = () => {
      // Show button after scrolling down 100px
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    // Check initial position
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [hideAtTop]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Open bottom sheet instead of scrolling
    setIsBottomSheetOpen(true);
  };

  if (!isVisible || !isScrolled) return null;

  return (
    <>
      <div className='fixed bottom-0 left-0 w-full flex flex-col bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.1)] z-50 px-4 py-4 md:hidden'>
        <div className='flex items-baseline mb-4 gap-1'>
          <span className='capitalize text-xs text-darkgrey font-normal'>
            From
          </span>

          <h2 className='text-2xl font-semibold leading-none tracking-tight'>
            <CurrencyDisplay
              amount={amount}
              backendCurrency={backendCurrency}
              backendConversionRate={backendConversionRate}
              showDecimals={false}
            />
          </h2>

          <span className='text-xs text-darkgrey font-normal'>/{amountby}</span>
        </div>

        <button
          type='button'
          className='btn btn-primary w-full px-[0.8125rem] py-[0.78125rem] leading-none flex items-center justify-center gap-2'
          onClick={handleClick}
        >
          {label}
        </button>
      </div>

      {/* Custom Bottom Sheet */}
      {isBottomSheetOpen && (
        <div className='fixed inset-0 z-[9999] md:hidden'>
          {/* Backdrop */}
          <div
            className='absolute inset-0 bg-black/50 transition-opacity duration-300'
            onClick={() => setIsBottomSheetOpen(false)}
          />

          {/* Bottom Sheet Container */}
          <div className='absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl transform transition-all duration-300 ease-out'>
            {/* Handle Bar */}
            <div className='flex justify-center pt-3 pb-2'>
              <div className='w-12 h-1 bg-gray-300 rounded-full' />
            </div>

            {/* Header */}
            <div className='px-4 pb-4 border-b'>
              <h3 className='text-lg font-semibold text-center'>
                Check Availability
              </h3>
            </div>

            {/* Content */}
            <div className='max-h-[85vh] overflow-y-auto pb-20'>
              {experience && type ? (
                <FloatingBookingForm
                  experience={experience}
                  type={type}
                  onTravelerCountChange={onTravelerCountChange}
                  inBottomSheet
                />
              ) : (
                <div className='flex items-center justify-center h-64 px-4'>
                  <div className='text-center'>
                    <div className='text-gray-500 mb-2'>
                      Loading booking form...
                    </div>
                    <div className='text-sm text-gray-400'>
                      Please wait a moment
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNavButton;
